import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useState,
} from 'react';
import { Snackbar } from '@cimpress/react-components';

export type SnackbarStatus = 'danger' | 'success' | 'warning' | 'info';

interface SnackbarState {
  show: boolean;
  message: string;
  status: SnackbarStatus;
}

interface SnackbarContextProps {
  showSnackbar: (message: string, status?: SnackbarStatus) => void;
  hideSnackbar: () => void;
}

const SnackbarContext = createContext<SnackbarContextProps | undefined>(
  undefined,
);

export const SnackbarProvider = ({ children }: { children: ReactNode }) => {
  const [snackbar, setSnackbar] = useState<SnackbarState>({
    show: false,
    message: '',
    status: 'info',
  });

  const showSnackbar = useCallback(
    (message: string, status: SnackbarStatus = 'info') => {
      setSnackbar({ show: true, message, status });
    },
    [],
  );

  const hideSnackbar = useCallback(() => {
    setSnackbar((prev) => ({ ...prev, show: false }));
  }, []);

  return (
    <SnackbarContext.Provider value={{ showSnackbar, hideSnackbar }}>
      {children}
      <Snackbar
        show={snackbar.show}
        status={snackbar.status}
        delay={5000}
        onHideSnackbar={hideSnackbar}
      >
        {snackbar.message}
      </Snackbar>
    </SnackbarContext.Provider>
  );
};

export const useSnackbar = (): SnackbarContextProps => {
  const context = useContext(SnackbarContext);
  if (!context) {
    throw new Error('useSnackbar must be used within a SnackbarProvider');
  }
  return context;
};
